import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import { HeadingTags } from '../../atoms/Heading/Heading.types';
import Input from '../../atoms/Input';
import { IncrementalInputProps, IncrementStep } from './IncrementalInput.types';
import styles from './IncrementalInput.module.scss';
import IconButton from '../IconButton';
import { IconButtonVariety } from '../IconButton/IconButton.types';
import Heading from '../../atoms/Heading';
import Amount from '../../atoms/Amount';

const incrementIconProps = {
  width: '18',
  height: '18',
  viewBox: '0 0 850 850',
  fill: '#FFF',
};

const IncrementalInput = ({
  name,
  value = 0,
  increment = IncrementStep.ONE,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  onChange,
  dataPointer = '',
  currency,
  locale,
  dataCy = 'incrementalInput',
  decrementBtnTrackingLabel = 'quote-customise-decrement',
  incrementBtnTrackingLabel = 'quote-customise-increment',
  className,
  label,
  disabled,
  invalid,
  errorMsgHolderId,
  id,
}: IncrementalInputProps) => {
  const didMountRef = useRef(false);
  const inputWrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [val, setVal] = useState<number>(Number(value));
  const [displayValue, setDisplayValue] = useState<number>(Number(value));
  const [showInput, setShowInput] = useState<boolean>(false);

  const valueWithBoundaries = (newValue: number) => {
    if (typeof newValue === 'number' && !Number.isNaN(newValue)) {
      if (newValue > max) {
        return max;
      }
      if (newValue < min) {
        return min;
      }
      return newValue;
    }
    return Number(value) || min;
  };

  const handleChange = (event) => {
    const newValue = Number(event.currentTarget.value);

    setVal(valueWithBoundaries(newValue));
  };

  const handleInputChange = (event) => {
    const newValue = Number(event.currentTarget.value);

    setDisplayValue(newValue);
  };

  const handleIncrement = () => {
    const newValue = Number(val) + increment;

    setVal(valueWithBoundaries(newValue));
  };

  const handleDecrement = () => {
    const newValue = Number(val) - increment;

    setVal(valueWithBoundaries(newValue));
  };

  const textValue = () => {
    if (currency)
      return (
        <Amount currency={currency} locale={locale} noDecimal>
          {val}
        </Amount>
      );
    return `${val}%`;
  };

  const handleClick = () => setShowInput(true);
  const handleBlur = (e) => {
    handleChange(e);
    setShowInput(false);
    setDisplayValue(val);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleChange(event);
      setShowInput(false);
    }

    if (event.key === '+' || event.key === 'ArrowUp') {
      event.preventDefault();

      handleIncrement();
    }

    if (event.key === '-' || event.key === 'ArrowDown') {
      event.preventDefault();

      handleDecrement();
    }
  };

  useEffect(() => setVal(Number(value)), [value]);

  useEffect(() => {
    if (showInput) {
      inputRef.current.focus();
    }
  }, [showInput, inputRef]);

  useEffect(() => {
    async function valChanged() {
      if (didMountRef.current) {
        setDisplayValue(val);
        onChange({ dataPointer, name, value: val });
      }
      didMountRef.current = true;
    }

    valChanged();
  }, [val]);

  return (
    <div className={cx(styles.incrementalContainer, className)} aria-errormessage={errorMsgHolderId}>
      <IconButton
        onClick={handleDecrement}
        variety={IconButtonVariety.SmallSquare}
        icon={{ icon: 'minus', title: 'decrement', ...incrementIconProps }}
        className={styles.incrementalButton}
        dataCy={dataCy}
        trackingLabel={decrementBtnTrackingLabel}
        label='decrease-amount'
        disabled={disabled}
      />
      <div className={styles.wrapper} ref={inputWrapperRef}>
        <Input
          label={`${label || 'Amount'}`}
          name={name}
          type='number'
          value={displayValue}
          onChange={handleInputChange}
          controlled
          data-pointer={dataPointer}
          className={cx(styles.input, styles.fakeHeading, {
            [styles.hiddenInput]: !showInput,
          })}
          onBlur={handleBlur}
          inputRef={inputRef}
          dataCy={dataCy}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          invalid={invalid}
          id={id}
        />
        <Heading
          tabIndex={0}
          onClick={handleClick}
          onFocus={handleClick}
          className={cx(styles.input, { [styles.hiddenInput]: showInput })}
          heading={HeadingTags.Two}>
          {textValue()}
        </Heading>
      </div>

      <IconButton
        onClick={handleIncrement}
        variety={IconButtonVariety.SmallSquare}
        icon={{ icon: 'plus', title: 'increment', ...incrementIconProps }}
        className={styles.incrementalButton}
        dataCy={dataCy}
        trackingLabel={incrementBtnTrackingLabel}
        label='increase-amount'
        disabled={disabled}
      />
    </div>
  );
};

export default IncrementalInput;
